import React, { useCallback, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { FaCheck, FaTimes } from 'react-icons/fa';
import useToolStore from '../../store/tools-store';
import useCrudListPage from '../../shared/hooks/useCrudListPage';
import DataTable from '../../shared/components/table/DataTable';
import PageHeader from '../../shared/components/table/PageHeader';
import SearchInput from '../../shared/components/table/SearchInput';
import RowActions from '../../shared/components/table/RowActions';
import DeleteModal from '../../shared/components/modal/DeleteModal';
import ToolsFormModal from './ToolsFormModal';
import { formatDate } from '../../shared/lib/formatters';

const columnHelper = createColumnHelper();

const emptyTool = {
  name: '',
  generation_name: '',
  speaker_labels: false,
  in_service: false,
  position: 0,
  fields: [{ name: '', position: 1 }],
  inputs: [{ type: '' }],
  outputs: [{ type: '' }],
};

const ToolsPage = () => {
  const { tools, fetchTools, createTool, updateTool, deleteTool } = useToolStore();
  const openEditRef = React.useRef(() => {});
  const openDeleteRef = React.useRef(() => {});

  const fetchToolsFresh = useCallback(() => fetchTools({ force: true }), [fetchTools]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', { header: 'ID' }),
      columnHelper.accessor('name', { header: 'Name' }),
      columnHelper.accessor('generation_name', {
        header: 'Generation Name',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
      }),
      columnHelper.accessor('position', {
        header: 'Position',
        cell: (info) => (info.getValue() ? info.getValue() : '0'),
      }),
      columnHelper.accessor('in_service', {
        header: 'In Service',
        cell: (info) => (info.getValue() === true ? <FaCheck className="text-green-500" /> : <FaTimes className="text-red-600" />),
      }),
      columnHelper.accessor('speaker_labels', {
        header: 'Speaker Lables',
        cell: (info) => (info.getValue() === true ? <FaCheck className="text-green-500" /> : <FaTimes className="text-red-600" />),
      }),
      columnHelper.accessor('created_at', {
        header: 'Created At',
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor('actions', {
        header: 'Actions',
        cell: ({ row }) => (
          <RowActions
            onEdit={() => openEditRef.current(row.original)}
            onDelete={() => openDeleteRef.current(row.original)}
          />
        ),
      }),
    ],
    []
  );

  const sortItems = useCallback((items) => [...items].sort((a, b) => a.position - b.position), []);

  const {
    table,
    isLoading,
    globalFilter,
    setGlobalFilter,
    isFormOpen,
    formMode,
    isDeleteModalOpen,
    selectedItem,
    deletePayload,
    openCreate,
    openEdit,
    openDelete,
    closeModals,
    handleFormSubmit,
    handleDelete,
  } = useCrudListPage({
    items: tools,
    fetchItems: fetchToolsFresh,
    createItem: createTool,
    updateItem: updateTool,
    deleteItem: deleteTool,
    columns,
    sortItems,
    entityLabel: 'Tool',
    getDeletePayload: (item) => ({ ...item, dataName: 'tool' }),
  });

  openEditRef.current = openEdit;
  openDeleteRef.current = openDelete;

  const initialValues = useMemo(() => {
    if (formMode === 'edit' && selectedItem) {
      return {
        name: selectedItem.name || '',
        generation_name: selectedItem.generation_name || '',
        speaker_labels: selectedItem.speaker_labels || false,
        in_service: selectedItem.in_service || false,
        position: selectedItem.position || 0,
        fields:
          selectedItem.fields && selectedItem.fields.length > 0
            ? selectedItem.fields.map((f) => ({ id: f.id, name: f.name, position: f.position }))
            : [{ name: '', position: 0 }],
        inputs:
          selectedItem.inputs && selectedItem.inputs.length > 0
            ? selectedItem.inputs.map((i) => ({ id: i.id, type: i.type }))
            : [{ type: '' }],
        outputs:
          selectedItem.outputs && selectedItem.outputs.length > 0
            ? selectedItem.outputs.map((o) => ({ id: o.id, type: o.type }))
            : [{ type: '' }],
      };
    }
    return emptyTool;
  }, [formMode, selectedItem]);

  return (
    <div className="max-w-screen-3xl mx-auto mt-4">
      <PageHeader title="Tool List" onCreate={openCreate} />
      <div className="mb-4">
        <SearchInput value={globalFilter} onChange={setGlobalFilter} />
      </div>
      <DataTable table={table} columnsCount={columns.length} isLoading={isLoading} emptyMessage="No tools found" />
      <ToolsFormModal
        isOpen={isFormOpen}
        onClose={closeModals}
        mode={formMode}
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
      />
      <DeleteModal isOpen={isDeleteModalOpen} onClose={closeModals} data={deletePayload} onDelete={handleDelete} />
    </div>
  );
};

export default ToolsPage;
